import { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { CAPABILITIES, getCapability } from "@/data/capabilities";
import { PROJECTS } from "@/data/projects";
import { SITE, url } from "@/data/site";
import PageShell from "@/components/PageShell";
import {
  CapabilityOthers,
  CapabilityPanels,
  CapabilityRelatedWork,
} from "@/components/capability/CapabilitySections";
import { PageTitle, Section } from "@/components/shared/Primitives";
import { track, EVENTS } from "@/lib/analytics";

const buildJsonLd = (capability, canonical) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: capability.name,
      url: canonical,
      description: capability.summary,
      serviceType: capability.name,
      provider: { "@id": `${SITE.base}/#organization` },
      areaServed: "Worldwide",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Elentara", item: url("/") },
        { "@type": "ListItem", position: 2, name: "Capabilities", item: url("/capabilities") },
        { "@type": "ListItem", position: 3, name: capability.name, item: canonical },
      ],
    },
  ],
});

export default function CapabilityDetail() {
  const { slug } = useParams();
  const capability = getCapability(slug);

  useEffect(() => {
    if (capability) track(EVENTS.CAPABILITY_VIEW, { capability: capability.slug });
  }, [capability]);

  const related = useMemo(
    () => (capability ? PROJECTS.filter((p) => p.capabilitySlug === capability.slug) : []),
    [capability],
  );

  const seo = useMemo(() => {
    if (!capability) return null;
    const canonical = url(`/capabilities/${capability.slug}`);
    return {
      title: capability.seoTitle,
      description: capability.seoDescription,
      canonical,
      image: related[0]?.image,
      jsonLd: buildJsonLd(capability, canonical),
    };
  }, [capability, related]);

  if (!capability) return <Navigate to="/capabilities" replace />;

  const others = CAPABILITIES.filter((c) => c.slug !== capability.slug);

  return (
    <PageShell
      testId="capability-detail-page"
      finalCtaFrom={`capability:${capability.slug}`}
      seo={seo}
    >
      <Section testId="capability-hero" className="!pt-36 !pb-12">
        <PageTitle
          overline={`Capability ${capability.id}`}
          testId="capability-title"
          title={capability.name}
          lead={capability.summary}
        />
      </Section>
      <CapabilityPanels capability={capability} />
      {related.length > 0 && <CapabilityRelatedWork projects={related} />}
      <CapabilityOthers others={others} />
    </PageShell>
  );
}
